/**
 * A form uploaded from an active procedure's toolbar (z8nrz7f030).
 *
 * Upload forms reads an Excel workbook with the form builder's own parser, then
 * saves the form and attaches it to the procedure being filled as an ad-hoc
 * form — the same attachment Add Form makes — so it appears in that
 * procedure's sidebar, ready to fill. The procedure's template is not changed.
 */
import type { SaveDefinitionPayload } from '@/api/formsApi'
import type { SectionDefinition } from '@/types/cgt'
import type { ParsedForm } from '@/lib/formExcel'
import { suggestFormHandle } from '@/lib/crossForm'

/** Only Excel workbooks belong in the bulk upload. */
export function isExcelFileName(name: string): boolean {
  return /\.(xlsx|xls)$/i.test(name.trim())
}

export const EXCEL_ONLY_MESSAGE = 'Only Excel files (.xlsx, .xls) can be uploaded here.'

/**
 * What saving an uploaded form sends: exactly what the builder sends for a
 * brand-new form, with the builder's defaults for everything the workbook does
 * not carry (no actions, no repeat, no completion gate or completer groups).
 *
 * `sections` arrives already converted to the saved shape — the builder's
 * `toSectionDefinitions` — so this module stays free of component imports.
 */
export function uploadedFormPayload(
  form: ParsedForm,
  sections: SectionDefinition[],
  suggestHandle: (name: string) => string = suggestFormHandle,
): SaveDefinitionPayload {
  const name = form.meta.name.trim() || 'Uploaded form'
  return {
    name,
    // Permanent once saved, and unique per form: the builder's own suggestion
    // (the name, hyphenated, plus a short random suffix).
    handle: suggestHandle(name),
    category: form.category,
    allowedActions: [],
    defaultRepeatCount: null,
    completerGroupIds: [],
    ...(form.fieldLayout ? { fieldLayout: form.fieldLayout } : {}),
    sections,
  } as SaveDefinitionPayload
}
